import type { toZod } from "tozod";
import { number, object, type SafeParseError, type SafeParseReturnType, type SafeParseSuccess } from "zod";

import type { Range } from "@/graphql.ts";

export const validateRegion = (
  contigLength: number,
  range: Range = {} as Range,
): [toZod<Range>, SafeParseReturnType<Range, Range>] => {
  const schema = object(
    {
      start: number({
        invalid_type_error: "The start position must be a number.",
        required_error: "Both start and stop position must be specified.",
      })
        .finite({ message: "The number is too big" })
        .int("Decimals are not allowed as start or stop coordinates.")
        .min(1, "The range start cannot be smaller than 1.")
        .max(range.stop, "The start position must be smaller than the stop position."),
      stop: number({
        invalid_type_error: "The start position must be a number.",
        required_error: "Both start and stop position must be specified.",
      })
        .finite({ message: "The number is too big" })
        .int("Decimals are not allowed as start or stop coordinates.")
        .min(range.start, "The stop position must be bigger than the start position.")
        .max(contigLength, `The stop value is bigger than the length of the contig (${contigLength} bases).`),
    },
    {
      invalid_type_error: "Both start and stop position must be specified.",
      required_error: "Required.",
    },
  );

  return [schema, schema.safeParse(range)];
};

if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest;
  describe("Validate Region", () => {
    it("should fail if no range values are provided", () => {
      const [, result1] = validateRegion(300);
      expect(result1.success).toBe(false);

      const [, result2] = validateRegion(300, { start: 45 } as Range);
      expect(result2.success).toBe(false);

      const [, result3] = validateRegion(300, { stop: 32 } as Range);
      expect(result3.success).toBe(false);
    });

    it("should pass with a correct range", () => {
      const range1 = { start: 1, stop: 300 };
      const [, result1] = validateRegion(300, range1);

      expect(result1.success).toBe(true);
      expect((result1 as SafeParseSuccess<Range>).data).toStrictEqual(range1);

      const range2 = { start: 56, stop: 157 };
      const [, result2] = validateRegion(300, range2);

      expect(result2.success).toBe(true);
      expect((result2 as SafeParseSuccess<Range>).data).toStrictEqual(range2);
    });

    it("should fail with zero start value", () => {
      const range = { start: 0, stop: 300 };
      const [, result] = validateRegion(300, range);

      expect(result.success).toBe(false);
      expect((result as SafeParseError<Range>).error).matchSnapshot();
    });

    it("should fail with stop value bigger than length", () => {
      const range = { start: 1, stop: 301 };
      const [, result] = validateRegion(300, range);

      expect(result.success).toBe(false);
      expect((result as SafeParseError<Range>).error).matchSnapshot();
    });

    it("should pass with a 1 base range", () => {
      const range = { start: 10, stop: 10 };
      const [, result] = validateRegion(300, range);

      expect(result.success).toBe(true);
      expect((result as SafeParseSuccess<Range>).data).toStrictEqual(range);
    });

    it("should fail with start bigger than stop", () => {
      const range = { start: 245, stop: 10 };
      const [, result] = validateRegion(300, range);

      expect(result.success).toBe(false);
      expect((result as SafeParseError<Range>).error).matchSnapshot();
    });
  });
}
